import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Preview-before-download hook for every PDF generator in the app.
 *
 * Callers get:
 *   - `preview({ fetcher | srcUrl, filename, title })` — trigger from a button
 *   - `dialog` — JSX to drop into render tree (renders nothing when idle)
 *
 * `fetcher` returns an axios response containing a Blob (authenticated).
 * `srcUrl` is a direct URL (public/unauthenticated PDFs).
 * Blob URLs are revoked when the dialog closes.
 */
export function usePdfPreview() {
  const [state, setState] = useState(null);

  const close = useCallback(() => {
    setState((s) => {
      if (s?.blobUrl) URL.revokeObjectURL(s.blobUrl);
      return null;
    });
  }, []);

  useEffect(() => () => {
    if (state?.blobUrl) URL.revokeObjectURL(state.blobUrl);
  }, [state?.blobUrl]);

  const preview = useCallback(async ({ fetcher, srcUrl, filename, title }) => {
    setState({ loading: true, title, filename });
    try {
      if (srcUrl) {
        setState({ loading: false, title, filename, url: srcUrl });
        return;
      }
      const resp = await fetcher();
      const ct = resp?.headers?.["content-type"] || "application/pdf";
      const blob = new Blob([resp.data], { type: ct });
      const blobUrl = URL.createObjectURL(blob);
      setState({ loading: false, title, filename, url: blobUrl, blobUrl });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed to load");
      setState(null);
    }
  }, []);

  // Use the plain blob URL — the `#toolbar=1&view=FitH` fragment DOES NOT
  // work reliably with blob: URLs in Chromium 128+ (viewer stops after page 1
  // or hangs on the loading spinner). `<object>` + `<embed>` fallback both
  // hand off to the native PDF viewer with full multi-page + scroll support.
  const viewerSrc = state?.url || null;

  const dialog = (
    <Dialog open={!!state} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] h-[94vh] sm:max-w-[96vw] p-0 flex flex-col overflow-hidden gap-0"
        data-testid="pdf-preview-dialog"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E9ECEF] bg-white shrink-0 pr-12">
          {/* pr-12 leaves room for the shadcn Dialog's built-in close X in the top-right corner —
              we deliberately DON'T add our own X so there's exactly one close button. */}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#D4AF37]">Preview</div>
            <div className="font-display text-lg font-black text-[#0B132B] truncate" data-testid="pdf-preview-title">
              {state?.title || "PDF preview"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {state?.url && (
              <>
                <a
                  href={state.url}
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 px-3 rounded-sm border border-[#0B132B]/20 text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B] inline-flex items-center gap-1.5 hover:bg-[#F8F9FA]"
                  data-testid="pdf-preview-new-tab"
                >
                  <ExternalLink size={12} /> New tab
                </a>
                <a
                  href={state.url}
                  download={state.filename || "document.pdf"}
                  className="h-9 px-3 rounded-sm bg-[#0B132B] text-white text-xs uppercase tracking-[0.16em] font-bold inline-flex items-center gap-1.5 hover:bg-[#1C2541]"
                  data-testid="pdf-preview-download"
                >
                  <Download size={12} /> Download
                </a>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-[#0B132B]/5">
          {state?.loading ? (
            <div className="h-full flex items-center justify-center gap-2 text-sm text-[#6C757D]" data-testid="pdf-preview-loading">
              <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
              <span>Generating preview — this can take up to a minute for large workbooks…</span>
            </div>
          ) : viewerSrc ? (
            <object
              data={viewerSrc}
              type="application/pdf"
              className="w-full h-full bg-white"
              data-testid="pdf-preview-iframe"
              aria-label={state.title || "PDF preview"}
            >
              <embed
                src={viewerSrc}
                type="application/pdf"
                className="w-full h-full bg-white"
              />
              <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-[#6C757D] p-8 text-center">
                <div>Your browser can't display PDFs inline.</div>
                <a
                  href={viewerSrc}
                  download={state.filename || "document.pdf"}
                  className="h-9 px-4 rounded-sm bg-[#0B132B] text-white text-xs uppercase tracking-[0.16em] font-bold inline-flex items-center gap-1.5 hover:bg-[#1C2541]"
                >
                  <Download size={12} /> Download PDF
                </a>
              </div>
            </object>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );

  return { preview, dialog };
}

export default usePdfPreview;
